import React, { useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import { SegmentListStates } from '../trigger-step/SegmentListStates';
import styles from './CheckAudienceDialog.module.css';

type CheckSegmentMembershipAction = Extract<
  AutomationTemplateContents['actionData'][number],
  { type: typeof BusinessAutomationActionTypeEnum.CHECK_SEGMENT_MEMBERSHIP }
>;

interface CheckAudienceDialogProps {
  open: boolean;
  onClose: () => void;
  action: CheckSegmentMembershipAction;
  index: number;
}

export const CheckAudienceDialog: React.FC<CheckAudienceDialogProps> = ({
  open,
  onClose,
  action,
  index,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction } = editor;
  const { data: segmentDefinitions, isLoading, error } = useSegmentDefinitions();

  const currentSegmentId = action.segmentIds?.[0] ?? null;
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(currentSegmentId);

  useEffect(() => {
    if (open) {
      setSelectedSegmentId(action.segmentIds?.[0] ?? null);
    }
  }, [open, action.segmentIds]);

  if (!open) return null;

  const handleOK = () => {
    updateAction(index, {
      segmentIds: selectedSegmentId ? [selectedSegmentId] : [],
    });
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <hgroup>
            <h2>Configure Check Audience</h2>
            <p>Select the audience segment to check</p>
          </hgroup>
          <button onClick={onClose} type="button" aria-label="Close">
            <X />
          </button>
        </header>

        <main>
          <div className={styles.sectionHeader}>Audience Segment</div>
          <p className={styles.sectionDescription}>
            If the customer is not in the selected segment, they will be removed from this campaign.
          </p>

          <SegmentListStates isLoading={isLoading} error={error} isEmpty={!segmentDefinitions?.length} />
          {segmentDefinitions && segmentDefinitions.length > 0 && (
            <ul>
              {segmentDefinitions.map(segment => {
                const { id, name, description, canonicalVersionId } = segment;
                const isSelected = selectedSegmentId === canonicalVersionId;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      data-selected={isSelected || undefined}
                      onClick={() => setSelectedSegmentId(canonicalVersionId)}>
                      <strong>{name}</strong>
                      {description && <p>{description}</p>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>
    </>
  );
};
